import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import Cookies from "js-cookie";
import { CompanyLogo, getUserFromToken, ToastMessage } from "../../Components/Utils";
import { FormInput } from "../../Components/Form";
import { ShineEffect } from "../../Components/Effects";
import { handleError } from "../../stores/components/handleError";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import {
  useLoginMutation,
  useFetchPermissionsMutation,
} from "../../stores/api/auth.api";
import payloadHelper from "../../stores/api/helpers/payload.helper";

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const navigate = useNavigate();
    const [login, { isLoading: loginLoading }] = useLoginMutation();
    const [fetchPermissions, { isLoading: permsLoading }] = useFetchPermissionsMutation();
    const loading = loginLoading || permsLoading;

   const handleSubmit = async (e) => {
    e.preventDefault();

        if (!formData.email || !formData.password) {
            ToastMessage({
                type: "error",
                message:
                    "Please enter " +
                    (!formData.email ? "email" : "password"),
                title: "Validation error!",
            });

            return;
        }

        try {
            const response = await login(payloadHelper.forLogin(formData)).unwrap();

            if (response.token) {
                Cookies.set("token", response.token, {
                    expires: 7,
                });
                const getUserData = getUserFromToken(response.token);
                const getPermission = await fetchPermissions(
                    payloadHelper.forFetchPermissions(getUserData.role_id)
                ).unwrap();
                if (getPermission) {
                    localStorage.setItem("permissions", JSON.stringify(getPermission));
                }
            }

            ToastMessage({
                type: "success",
                message: "Login successful!",
                title: "Welcome back!",
            });
            navigate(ROUTE_PATHS.DASHBOARD);

        } catch (error) {
            handleError(error, "Login failed. Please check your credentials and try again.");
        }
    };

    return (
      <div className="mx-auto w-full max-w-md">
        <div className="rounded-[2rem] bg-white/95 p-5 shadow-2xl shadow-black/10 ring-1 ring-slate-200/80 dark:bg-slate-900/95 dark:shadow-slate-950/20 dark:ring-slate-800/70">
          <div className="mb-8 text-center">
            <div className="mx-auto flex h-16 w-auto min-w-16 max-w-[200px] items-center justify-center rounded-3xl bg-slate-100 dark:bg-slate-800 px-4 shadow-inner shadow-slate-200/40 dark:shadow-slate-950/30">
              <CompanyLogo className="h-10 w-auto max-w-full" />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.35em] text-slate-500 dark:text-slate-400">Admin Portal</p>
            <h2 className="mt-4 text-3xl font-semibold text-slate-900 dark:text-white">Welcome back</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">Sign in to access the MHU administration dashboard.</p>
          </div>

          <form className="space-y-3" onSubmit={handleSubmit}>
            <FormInput
              name="email"
              value={formData.email}
              onChange={(val) => setFormData((prev) => ({ ...prev, email: val }))}
              label="Email"
              placeholder="you@example.com"
              type="email"
              required
              className="p-1"
            />
            <FormInput
              name="password"
              value={formData.password}
              onChange={(val) => setFormData((prev) => ({ ...prev, password: val }))}
              label="Password"
              placeholder="Enter your password"
              type="password"
              required
              className="p-1"
            />

            <div className="flex items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400">
              <p>Secure admin access</p>
              <Link
                to={ROUTE_PATHS.RESET_PASSWORD}
                className="font-semibold text-green-400 hover:text-green-300 transition duration-200"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-green-500 px-4 py-2.5 text-sm font-semibold text-white shadow-green-500/30 transition duration-200 hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                </svg>
              )}
              {loading ? "Authenticating..." : "Login"}
            </button>
          </form>

          <div className="mt-8 rounded-3xl bg-slate-100/80 p-4 text-center text-sm text-slate-600 dark:bg-slate-900/80 dark:text-slate-400">
            If you need help signing in, contact your administrator for support.
          </div>
        </div>
      </div>
    );
};

export default Login;
